import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UnifiedLoginModal } from '../pages/authentication/components/UnifiedLoginModal';
import { Box, Heading, Text, VStack, Button, Badge, Input, Tooltip, useColorMode, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { SEARCH_CLIENTS, ADD_CLIENT_PERMISSION, REMOVE_CLIENT_PERMISSION } from '../pages/clients/queries';
import { Client, ClientPermission } from '../generated/graphql';
import { getColor } from '../brandConfig';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  /**
   * An above-the-fold teaser for a logged-out visitor: what this page is, in
   * prose the module DELIBERATELY WROTE for strangers.
   *
   * ⚠️ Never render the real page blurred, cropped or faded to achieve this.
   * Blur and max-height are CSS — the page would still be in the DOM, which is
   * the exact leak the block below exists to close, wearing a different hat.
   * A teaser source file must not import a page component.
   *
   * When a teaser is present the login modal starts CLOSED behind a button:
   * auto-opening it covers the very thing the visitor came to read.
   */
  preview?: { title: string; blurb: string };
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  preview
}) => {
  const { isAuthenticated, user, loading, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  /**
   * Whether the sign-in modal is showing. Only consulted when a teaser exists
   * — without one the modal opens immediately, because there is nothing for
   * the visitor to read first.
   */
  const [loginOpen, setLoginOpen] = useState(false);
  const devBadgeHiddenByDefault = process.env.REACT_APP_HIDE_DEV_BADGE === 'true';

  // Dev badge visibility (persisted in localStorage, env var sets default only)
  const [showDevBadge, setShowDevBadge] = useState(() => {
    if (!isDev) return false;
    const stored = localStorage.getItem('devBadgeVisible');
    if (stored !== null) return stored === 'true';
    return !devBadgeHiddenByDefault;
  });

  const toggleDevBadge = (visible: boolean) => {
    setShowDevBadge(visible);
    localStorage.setItem('devBadgeVisible', String(visible));
  };

  // Press 'D' to toggle the dev badge
  useEffect(() => {
    if (!isDev) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'd' || e.key === 'D') {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable) return;
        toggleDevBadge(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDev]);

  // Dev badge state
  const [activePermission, setActivePermission] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [badgePos, setBadgePos] = useState({ x: window.innerWidth - 340, y: window.innerHeight - 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedClient, setSelectedClient] = useState<Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'> | null>(null);
  const [showMyPerms, setShowMyPerms] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');

  // Impersonation state — persisted in sessionStorage (per-tab, not per-browser)
  const IMPERSONATION_KEY = 'devBadgeImpersonation';
  type ImpersonationSnapshot = {
    originalPerms: string[];
    targetClientId: string;
    targetClientName: string;
    targetClientEmail?: string | null;
    startedAt: number;
  };
  const [impersonation, setImpersonation] = useState<ImpersonationSnapshot | null>(() => {
    try {
      const raw = sessionStorage.getItem(IMPERSONATION_KEY);
      return raw ? (JSON.parse(raw) as ImpersonationSnapshot) : null;
    } catch {
      return null;
    }
  });
  const [impersonationBusy, setImpersonationBusy] = useState(false);

  const persistImpersonation = (snap: ImpersonationSnapshot | null) => {
    setImpersonation(snap);
    if (snap) sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(snap));
    else sessionStorage.removeItem(IMPERSONATION_KEY);
  };

  // Clear snapshot if the logged-in user changes (logout/re-login while impersonating)
  useEffect(() => {
    if (impersonation && user && user.id !== impersonation.targetClientId && !impersonationBusy) {
      // Only clear if we're not the owner of the snapshot's original perms — i.e.
      // user id mismatch means the session swapped. Leave sessionStorage alone; just
      // drop the in-memory state so we don't show a stale banner for a stranger.
      setImpersonation(null);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchClients, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_CLIENTS);
  const [compareSearchClients, { data: compareData, loading: compareLoading }] = useLazyQuery(SEARCH_CLIENTS);
  const [addPermission] = useMutation(ADD_CLIENT_PERMISSION);
  const [removePermission] = useMutation(REMOVE_CLIENT_PERMISSION);

  // Drag handlers for dev badge
  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.dev-badge-drag-handle')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - badgePos.x, y: e.clientY - badgePos.y });
    }
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      setBadgePos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, dragStart]);

  const handleBadgeClick = (permission: string) => {
    if (activePermission === permission) {
      setActivePermission(null);
      setSearchTerm('');
    } else {
      setActivePermission(permission);
      setSearchTerm('');
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      searchClients({ variables: { search: value } });
    }
  };

  const handleGrantPermission = async (clientId: string, clientName: string, permission?: string) => {
    const perm = permission || activePermission;
    try {
      await addPermission({
        variables: { clientId, permission: perm }
      });
      toast({
        title: `Added ${perm} to ${clientName}`,
        status: 'success',
        duration: 3000,
      });
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({
          ...selectedClient,
          permissions: [...(selectedClient.permissions || []), perm as ClientPermission]
        });
      }
      if (!permission) {
        setActivePermission(null);
        setSearchTerm('');
      }
      if (clientId === user?.id) refreshAuth();
    } catch (err: unknown) {
      toast({
        title: 'Failed to add permission',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleRemovePermission = async (clientId: string, clientName: string, permission: string) => {
    try {
      await removePermission({ variables: { clientId, permission } });
      toast({
        title: `Removed ${permission} from ${clientName}`,
        status: 'warning',
        duration: 3000,
      });
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({
          ...selectedClient,
          permissions: (selectedClient.permissions || []).filter(p => p !== permission)
        });
      }
      if (clientId === user?.id) refreshAuth();
    } catch (err: unknown) {
      toast({
        title: 'Failed to remove permission',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleCompareSearch = (value: string) => {
    setCompareSearch(value);
    if (value.length >= 2) {
      compareSearchClients({ variables: { search: value } });
    }
  };

  const handleSelectClient = (client: Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'>) => {
    setSelectedClient(client);
    setCompareSearch('');
  };

  // Alphabetical case-insensitive sort helper for perm display
  const sortPerms = (perms: readonly string[]): string[] =>
    [...perms].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // Impersonate = copy target client's permissions onto my own dev session.
  // Records snapshot so we can restore with a reverse diff.
  const handleImpersonate = async () => {
    if (!selectedClient || !user?.id) return;
    if (selectedClient.id === user.id) {
      toast({ title: "You're already yourself", status: 'info', duration: 2000 });
      return;
    }
    if (impersonation) {
      toast({ title: 'Already impersonating', description: 'Restore first via the yellow banner.', status: 'warning', duration: 4000 });
      return;
    }
    const targetPerms = ((selectedClient.permissions || []) as string[]).slice();
    const myCurrent = myPerms.slice();
    const toAdd = targetPerms.filter((p) => !myCurrent.includes(p));
    const toRemove = myCurrent.filter((p) => !targetPerms.includes(p));

    const adminBlocked = myCurrent.includes('ADMIN') && !targetPerms.includes('ADMIN');
    const proceed = window.confirm(
      `Mirror ${selectedClient.fName} ${selectedClient.lName}'s permissions onto your dev session?\n\n` +
      `+ Add ${toAdd.length} permission${toAdd.length === 1 ? '' : 's'}\n` +
      `− Remove ${toRemove.length} permission${toRemove.length === 1 ? '' : 's'}` +
      (adminBlocked ? '\n\n⚠️ You will lose ADMIN. Restore later via the banner.' : '')
    );
    if (!proceed) return;

    setImpersonationBusy(true);
    try {
      for (const p of toAdd) {
        await addPermission({ variables: { clientId: user.id, permission: p } });
      }
      for (const p of toRemove) {
        await removePermission({ variables: { clientId: user.id, permission: p } });
      }
      persistImpersonation({
        originalPerms: myCurrent,
        targetClientId: selectedClient.id,
        targetClientName: `${selectedClient.fName} ${selectedClient.lName}`,
        targetClientEmail: selectedClient.email,
        startedAt: Date.now(),
      });
      await refreshAuth();
      toast({
        title: `Now impersonating ${selectedClient.fName} ${selectedClient.lName}`,
        description: 'Restore via the yellow banner at the top of the page.',
        status: 'success',
        duration: 5000,
      });
    } catch (err: unknown) {
      toast({
        title: 'Impersonation failed',
        description: err instanceof Error ? err.message : 'Unknown error. Some mutations may have landed — check My Perms and restore manually.',
        status: 'error',
        duration: 8000,
      });
    } finally {
      setImpersonationBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!impersonation || !user?.id) return;
    const current = myPerms.slice();
    const original = impersonation.originalPerms.slice();
    const toAdd = original.filter((p) => !current.includes(p));
    const toRemove = current.filter((p) => !original.includes(p));

    setImpersonationBusy(true);
    try {
      for (const p of toAdd) {
        await addPermission({ variables: { clientId: user.id, permission: p } });
      }
      for (const p of toRemove) {
        await removePermission({ variables: { clientId: user.id, permission: p } });
      }
      persistImpersonation(null);
      await refreshAuth();
      toast({ title: 'Restored your permissions', status: 'success', duration: 3000 });
    } catch (err: unknown) {
      toast({
        title: 'Restore failed',
        description: err instanceof Error ? err.message : 'Some mutations may have landed — check My Perms.',
        status: 'error',
        duration: 8000,
      });
    } finally {
      setImpersonationBusy(false);
    }
  };

  // Wait for auth to load
  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  // ── SEAMLESS PAGE AUTH ────────────────────────────────────────────────
  //
  // Show the login modal in place, at the same URL, instead of bouncing the
  // visitor to the home page. After they sign in the page renders immediately.
  //
  // ⚠️ RENDER THE PLACEHOLDER *INSTEAD OF* `children` — NEVER ALONGSIDE IT.
  //
  // Until 2026-09-07 this returned `<>{children}<UnifiedLoginModal/></>`, which
  // looks identical on screen — modal on top, nothing clickable — while the
  // entire protected page sat in the DOM for devtools, view-source, a screen
  // reader and disabled CSS to read straight past.
  //
  // Server DATA was never exposed (every resolver gates on authMiddleware +
  // hasPermission, and Shelv reads additionally filter by kitchenId), but the
  // static markup was: headings, form labels, empty states, upsell copy. That
  // is survivable right up until a page whose MARKUP is the product renders
  // through here — which is why this is fixed in the shared component rather
  // than page by page.
  //
  // Regression grep, from vault/how-to/seamless-page-auth-pattern.md:
  //   awk '/if \(!isAuthenticated\)/{f=1} f{print} /^  \}/{if(f)exit}' \
  //     src/components/ProtectedRoute.tsx | grep "{children}"
  // Any hit means the leak is back.
  if (!isAuthenticated) {
    return (
      <Box
        minH="100vh"
        bg={getColor('background.main', colorMode)}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
      >
        <VStack spacing={3} textAlign="center" maxW="lg">
          {preview ? (
            <>
              <Heading size="lg" color={getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode)}>
                {preview.title}
              </Heading>
              <Text
                color={getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode)}
                fontSize="md"
                lineHeight="tall"
              >
                {preview.blurb}
              </Text>
              <Tooltip label="Opens the sign-in box. You'll land right back on this page.">
                <Button
                  mt={4}
                  colorScheme="green"
                  onClick={() => setLoginOpen(true)}
                  whiteSpace="normal"
                  height="auto"
                  py={3}
                  maxW="100%"
                >
                  Sign in to see this
                </Button>
              </Tooltip>
              <Text color={getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode)} fontSize="xs">
                You'll land right back on this page.
              </Text>
            </>
          ) : (
            <>
              <Heading size="lg" color={getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode)}>
                Sign in to continue
              </Heading>
              <Text color={getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode)}>
                Please sign in to view this page.
              </Text>
            </>
          )}
        </VStack>
        {/* No teaser → nothing to read first, so open immediately (the old
            behaviour). A teaser → start CLOSED, or the modal covers it. */}
        <UnifiedLoginModal
          isOpen={preview ? loginOpen : true}
          onClose={() => setLoginOpen(false)}
          onSuccess={(token: string) => {
            localStorage.setItem('auth_token', token);
            refreshAuth();
          }}
        />
      </Box>
    );
  }

  // Check if user has required permissions
  if (requiredPermissions.length > 0) {
    // A MASTER_ADMIN is waved past every gate elsewhere on the platform, so
    // refusing them here would deny an operator exactly the pages they exist
    // to be able to open. `checkPermissions` has no implicit bypass, which is
    // why this has to be explicit rather than assumed.
    const isMasterAdmin = user?.permissions?.includes('MASTER_ADMIN');
    const hasPermission = isMasterAdmin || requiredPermissions.some(permission =>
      user?.permissions?.includes(permission)
    );

    if (!hasPermission) {
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={getColor('background.main', colorMode)}
        >
          <VStack spacing={4} textAlign="center" p={8}>
            <Heading size="lg" color="red.500">Access Denied</Heading>
            <Text color={getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode)}>
              You don't have permission to access this page.
            </Text>
            <Text fontSize="sm" color={getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode)}>
              Required permissions: {requiredPermissions.join(', ')}
            </Text>
            <Button
              colorScheme="blue"
              onClick={() => navigate('/')}
              mt={4}
            >
              Go to Home
            </Button>
          </VStack>
        </Box>
      );
    }
  }

  const clients = searchData?.searchClients || [];
  const compareClients = compareData?.searchClients || [];
  const myPerms: string[] = user?.permissions || [];
  // OR-based: having ANY required permission grants access
  const myRouteAccess = requiredPermissions.length === 0 || requiredPermissions.some(p => myPerms.includes(p));
  const myGrantingPerms = requiredPermissions.filter(p => myPerms.includes(p));

  return (
    <>
      {children}
      {isDev && showDevBadge && (
        <Box
          position="fixed"
          left={`${badgePos.x}px`}
          top={`${badgePos.y}px`}
          bg={colorMode === 'light' ? 'gray.800' : 'gray.700'}
          color="white"
          p={3}
          borderRadius="md"
          fontSize="xs"
          opacity={0.8}
          maxW="360px"
          maxH="80vh"
          overflowY="auto"
          zIndex={9999}
          boxShadow="lg"
          _hover={{ opacity: 1 }}
          onMouseDown={handleDragStart}
          userSelect={isDragging ? 'none' : 'auto'}
        >
          <VStack align="start" spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" w="100%">
              <Text
                className="dev-badge-drag-handle"
                fontWeight="bold"
                fontSize="xs"
                color="yellow.300"
                cursor="grab"
              >
                ⠿ DEV: {location.pathname}
              </Text>
              <Text
                fontSize="xs"
                color="gray.400"
                cursor="pointer"
                onClick={() => toggleDevBadge(false)}
                _hover={{ color: 'red.300' }}
                title="Hide dev badge (press D to bring back)"
                ml={2}
              >
                ✕
              </Text>
            </Box>

            {/* Session identity — who am I right now? */}
            <Box w="100%" fontSize="8px" color="gray.400" lineHeight="1.3">
              <Text>
                as <Text as="span" color="white">{user?.email || user?.phone || user?.id}</Text>
                {user?.fName && <Text as="span"> · {user.fName} {user.lName}</Text>}
              </Text>
            </Box>

            {/* Impersonation banner (sticky inside badge) */}
            {impersonation && (
              <Box
                w="100%"
                p={2}
                bg="yellow.700"
                color="yellow.50"
                borderRadius="md"
                border="2px solid"
                borderColor="yellow.300"
              >
                <Text fontSize="xs" fontWeight="bold" mb={1}>
                  ⚠ Impersonating {impersonation.targetClientName}
                </Text>
                {impersonation.targetClientEmail && (
                  <Text fontSize="8px" color="yellow.100" mb={1}>{impersonation.targetClientEmail}</Text>
                )}
                <Text fontSize="8px" color="yellow.100" mb={2}>
                  Started {new Date(impersonation.startedAt).toLocaleTimeString()}. Your original perms are snapshotted.
                </Text>
                <Button
                  size="xs"
                  colorScheme="yellow"
                  variant="solid"
                  onClick={handleRestore}
                  isLoading={impersonationBusy}
                  loadingText="Restoring..."
                  w="100%"
                >
                  Restore my perms
                </Button>
              </Box>
            )}

            {/* Route required permissions */}
            {requiredPermissions.length > 0 && (
              <>
                <Text fontSize="xs" color="gray.300">Required permissions: <Text as="span" fontSize="7px" color="gray.500">(any one grants access)</Text></Text>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {sortPerms(requiredPermissions).map((p) => {
                    const hasIt = myPerms.includes(p);
                    const badgeColor = hasIt ? 'green' : (myRouteAccess ? 'gray' : 'red');
                    const icon = hasIt ? '✓' : (myRouteAccess ? '–' : '✗');
                    const tooltip = hasIt
                      ? `You have ${p} — click to grant to a client`
                      : myRouteAccess
                        ? `Not needed — access via ${myGrantingPerms.join(', ')}. Click to grant to a client`
                        : `Missing ${p} — click to grant to a client`;
                    return (
                      <Tooltip key={p} label={tooltip} fontSize="8px" placement="top" hasArrow>
                        <Badge
                          colorScheme={badgeColor}
                          fontSize="8px"
                          variant="subtle"
                          cursor="pointer"
                          onClick={() => handleBadgeClick(p)}
                          _hover={{ opacity: 0.7 }}
                          outline={activePermission === p ? '2px solid white' : 'none'}
                        >
                          {icon} {p}
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </Box>
              </>
            )}

            {/* Grant permission search (existing flow) */}
            {activePermission && (
              <Box
                p={2}
                bg="gray.900"
                borderRadius="md"
                w="100%"
                border="1px solid"
                borderColor="yellow.300"
              >
                <Text fontSize="xs" color="yellow.300" mb={1}>
                  Grant <strong>{activePermission}</strong> to:
                </Text>
                <Input
                  size="xs"
                  placeholder="Search client by name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  bg="gray.700"
                  border="none"
                  color="white"
                  mb={1}
                  autoFocus
                />
                {searchLoading && (
                  <Text fontSize="xs" color="gray.400">Searching...</Text>
                )}
                {searchTerm.length >= 2 && clients.length > 0 && (
                  <VStack align="stretch" spacing={0} maxH="120px" overflowY="auto">
                    {clients.slice(0, 5).map((c: Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'>) => {
                      const perms = c.permissions || [];
                      const hasTarget = activePermission ? perms.includes(activePermission as ClientPermission) : false;
                      return (
                        <Tooltip
                          key={c.id}
                          label={perms.length > 0 ? `Permissions (${perms.length}):\n${perms.join(', ')}` : 'No permissions'}
                          placement="left"
                          fontSize="8px"
                          maxW="250px"
                          whiteSpace="pre-wrap"
                          hasArrow
                        >
                          <Box
                            px={2}
                            py={1}
                            cursor="pointer"
                            borderRadius="sm"
                            _hover={{ bg: 'gray.600' }}
                            onClick={() => handleGrantPermission(c.id, `${c.fName} ${c.lName}`)}
                          >
                            <Text fontSize="xs" color="white">
                              {c.fName} {c.lName}
                              {hasTarget && (
                                <Badge ml={1} colorScheme="yellow" fontSize="7px" variant="subtle">
                                  already has
                                </Badge>
                              )}
                            </Text>
                            <Text fontSize="8px" color="gray.400">{c.email}</Text>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </VStack>
                )}
                {searchTerm.length >= 2 && !searchLoading && clients.length === 0 && (
                  <Text fontSize="xs" color="gray.400">No clients found</Text>
                )}
                <Text
                  fontSize="8px"
                  color="gray.500"
                  mt={1}
                  cursor="pointer"
                  onClick={() => { setActivePermission(null); setSearchTerm(''); }}
                  _hover={{ color: 'gray.300' }}
                >
                  [cancel]
                </Text>
              </Box>
            )}

            {/* My Permissions toggle */}
            <Text
              fontSize="xs"
              color="cyan.300"
              cursor="pointer"
              onClick={() => setShowMyPerms(!showMyPerms)}
              _hover={{ color: 'cyan.200' }}
            >
              {showMyPerms ? '▼' : '▶'} My Perms ({myPerms.length})
            </Text>
            {showMyPerms && (
              <Box p={2} bg="gray.900" borderRadius="md" w="100%" border="1px solid" borderColor="cyan.600">
                <Box display="flex" flexWrap="wrap" gap={1} maxH="150px" overflowY="auto">
                  {sortPerms(myPerms).map((p) => {
                    const isRequired = requiredPermissions.includes(p);
                    return (
                      <Tooltip key={p} label={`Click to remove ${p} from yourself`} fontSize="8px" placement="top" hasArrow>
                        <Badge
                          colorScheme={isRequired ? 'green' : 'gray'}
                          fontSize="7px"
                          variant="subtle"
                          cursor="pointer"
                          onClick={() => handleRemovePermission(user?.id || '', 'Me', p)}
                          _hover={{ opacity: 0.7 }}
                        >
                          {p} ✗
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </Box>
                {requiredPermissions.filter(p => !myPerms.includes(p)).length > 0 && (
                  <Box mt={2}>
                    {myRouteAccess ? (
                      <>
                        <Text fontSize="8px" color="green.300">
                          Access granted via: {myGrantingPerms.join(', ')}
                        </Text>
                        <Text fontSize="7px" color="gray.500" mt={1}>
                          Other accepted permissions (click to add):
                        </Text>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {sortPerms(requiredPermissions.filter(p => !myPerms.includes(p))).map((p) => (
                            <Tooltip key={p} label={`Click to add ${p} to yourself (optional — you already have access)`} fontSize="8px" placement="top" hasArrow>
                              <Badge
                                colorScheme="gray"
                                fontSize="7px"
                                variant="subtle"
                                cursor="pointer"
                                onClick={() => handleGrantPermission(user?.id || '', 'Me', p)}
                                _hover={{ opacity: 0.7 }}
                              >
                                + {p}
                              </Badge>
                            </Tooltip>
                          ))}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Text fontSize="8px" color="red.300">Missing for this route:</Text>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {sortPerms(requiredPermissions.filter(p => !myPerms.includes(p))).map((p) => (
                            <Tooltip key={p} label={`Click to add ${p} to yourself`} fontSize="8px" placement="top" hasArrow>
                              <Badge
                                colorScheme="red"
                                fontSize="7px"
                                variant="subtle"
                                cursor="pointer"
                                onClick={() => handleGrantPermission(user?.id || '', 'Me', p)}
                                _hover={{ opacity: 0.7 }}
                              >
                                + {p}
                              </Badge>
                            </Tooltip>
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Compare Client section */}
            <Box w="100%" borderTop="1px solid" borderColor="gray.600" pt={2}>
              <Text fontSize="xs" color="orange.300" mb={1}>Compare Client:</Text>
              {selectedClient ? (
                <Box p={2} bg="gray.900" borderRadius="md" border="1px solid" borderColor="orange.600">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Text fontSize="xs" color="orange.300" fontWeight="bold">
                      {selectedClient.fName} {selectedClient.lName}
                    </Text>
                    <Text
                      fontSize="8px"
                      color="gray.500"
                      cursor="pointer"
                      onClick={() => setSelectedClient(null)}
                      _hover={{ color: 'gray.300' }}
                    >
                      [clear]
                    </Text>
                  </Box>
                  {selectedClient.email && (
                    <Text fontSize="8px" color="gray.400" mb={1}>{selectedClient.email}</Text>
                  )}
                  <Button
                    size="xs"
                    colorScheme="yellow"
                    variant="outline"
                    w="100%"
                    mb={2}
                    onClick={handleImpersonate}
                    isLoading={impersonationBusy}
                    loadingText="Mirroring..."
                    isDisabled={!!impersonation || selectedClient.id === user?.id}
                    title={
                      impersonation
                        ? 'Already impersonating — restore first'
                        : selectedClient.id === user?.id
                          ? "That's you"
                          : `Mirror ${selectedClient.fName}'s perms onto your dev session`
                    }
                  >
                    🎭 Mirror their perms (impersonate)
                  </Button>
                  <Text fontSize="8px" color="gray.400" mb={1}>
                    Permissions ({(selectedClient.permissions || []).length}):
                  </Text>
                  <Box display="flex" flexWrap="wrap" gap={1} maxH="150px" overflowY="auto">
                    {sortPerms((selectedClient.permissions || []) as string[]).map((p) => {
                      const isRequired = requiredPermissions.includes(p as string);
                      return (
                        <Tooltip key={p as string} label={`Click to remove ${p} from ${selectedClient.fName}`} fontSize="8px" placement="top" hasArrow>
                          <Badge
                            colorScheme={isRequired ? 'green' : 'blue'}
                            fontSize="7px"
                            variant="subtle"
                            cursor="pointer"
                            onClick={() => handleRemovePermission(selectedClient.id, `${selectedClient.fName}`, p as string)}
                            _hover={{ opacity: 0.7 }}
                          >
                            {p} ✗
                          </Badge>
                        </Tooltip>
                      );
                    })}
                  </Box>
                  {/* Missing permissions for this route */}
                  {(() => {
                    const clientPerms = (selectedClient.permissions || []) as string[];
                    const clientHasAccess = requiredPermissions.length === 0 || requiredPermissions.some(p => clientPerms.includes(p));
                    const clientGrantingPerms = requiredPermissions.filter(p => clientPerms.includes(p));
                    const clientMissing = requiredPermissions.filter(p => !clientPerms.includes(p));
                    if (clientMissing.length === 0) return null;
                    return (
                      <Box mt={2}>
                        {clientHasAccess ? (
                          <>
                            <Text fontSize="8px" color="green.300">
                              Access granted via: {clientGrantingPerms.join(', ')}
                            </Text>
                            <Text fontSize="7px" color="gray.500" mt={1}>
                              Other accepted permissions (click to add):
                            </Text>
                            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                              {sortPerms(clientMissing).map((p) => (
                                <Tooltip key={p} label={`Click to add ${p} to ${selectedClient.fName} (optional — already has access)`} fontSize="8px" placement="top" hasArrow>
                                  <Badge
                                    colorScheme="gray"
                                    fontSize="7px"
                                    variant="subtle"
                                    cursor="pointer"
                                    onClick={() => handleGrantPermission(selectedClient.id, `${selectedClient.fName}`, p)}
                                    _hover={{ opacity: 0.7 }}
                                  >
                                    + {p}
                                  </Badge>
                                </Tooltip>
                              ))}
                            </Box>
                          </>
                        ) : (
                          <>
                            <Text fontSize="8px" color="red.300">Missing for this route:</Text>
                            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                              {sortPerms(clientMissing).map((p) => (
                                <Tooltip key={p} label={`Click to add ${p} to ${selectedClient.fName}`} fontSize="8px" placement="top" hasArrow>
                                  <Badge
                                    colorScheme="red"
                                    fontSize="7px"
                                    variant="subtle"
                                    cursor="pointer"
                                    onClick={() => handleGrantPermission(selectedClient.id, `${selectedClient.fName}`, p)}
                                    _hover={{ opacity: 0.7 }}
                                  >
                                    + {p}
                                  </Badge>
                                </Tooltip>
                              ))}
                            </Box>
                          </>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              ) : (
                <Box>
                  <Input
                    size="xs"
                    placeholder="Search client to compare..."
                    value={compareSearch}
                    onChange={(e) => handleCompareSearch(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    mb={1}
                  />
                  {compareLoading && (
                    <Text fontSize="xs" color="gray.400">Searching...</Text>
                  )}
                  {compareSearch.length >= 2 && compareClients.length > 0 && (
                    <VStack align="stretch" spacing={0} maxH="120px" overflowY="auto">
                      {compareClients.slice(0, 5).map((c: Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'>) => (
                        <Box
                          key={c.id}
                          px={2}
                          py={1}
                          cursor="pointer"
                          borderRadius="sm"
                          _hover={{ bg: 'gray.600' }}
                          onClick={() => handleSelectClient(c)}
                        >
                          <Text fontSize="xs" color="white">
                            {c.fName} {c.lName}
                            <Badge ml={1} colorScheme="blue" fontSize="7px" variant="subtle">
                              {(c.permissions || []).length} perms
                            </Badge>
                          </Text>
                          <Text fontSize="8px" color="gray.400">{c.email}</Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                  {compareSearch.length >= 2 && !compareLoading && compareClients.length === 0 && (
                    <Text fontSize="xs" color="gray.400">No clients found</Text>
                  )}
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
      )}
    </>
  );
};
